import * as React from "react";
import { useNavigate } from "react-router-dom";
import MUIDataTable, {
  MUIDataTableColumn,
  MUIDataTableOptions,
} from "mui-datatables";
import { createTheme, ThemeProvider, Theme } from "@mui/material/styles";
import {
  Autocomplete,
  Box,
  Card,
  FormControl,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";
import axios from "../../api";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import SoftBox from "components/SoftBox";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftTypography from "components/SoftTypography";
import { AxiosError } from "axios";
import { useSelector } from "react-redux";
import { RootState } from "store";
import VisibilityIcon from "@mui/icons-material/Visibility";

// Theme configuration
const getMuiTheme = (theme: Theme): Theme =>
  createTheme({
    components: {
      MUIDataTable: {
        styleOverrides: {
          responsiveScroll: {
            maxHeight: "unset",
            overflowX: "auto",
          },
        },
      },
    },
  });

// Table options
const tableOptions: MUIDataTableOptions = {
  selectableRows: "none",
  selectableRowsHeader: false,
  elevation: 0,
  tableBodyHeight: "auto",
  responsive: "standard",
};

interface AssetTypeItem {
  asset_type_id: number;
  asset_type_name: string;
}

type DateInput = string | number | Date;

// Component definitions moved OUTSIDE parent component
interface StatusCellProps {
  value: string | boolean;
}

const StatusCell: React.FC<StatusCellProps> = ({ value }) => (
  <span
    className="active-btn"
    style={{ borderColor: value ? "#00bdaa" : "#fe346e" }}
  >
    {value ? "Active" : "Inactive"}
  </span>
);

interface DateCellProps {
  value: DateInput;
}

const DateCell: React.FC<DateCellProps> = ({ value }) => {
  const d = new Date(value);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return <span>{`${year}-${month}-${day}`}</span>;
};

interface ActionCellProps {
  subscriptionId: number;
  navigate: (path: string) => void;
}

const ActionCell: React.FC<ActionCellProps> = ({
  subscriptionId,
  navigate,
}) => {
  return (
    <Box>
      <IconButton
        aria-label="view"
        onClick={() => navigate(`/view-subscription/${subscriptionId}`)}
      >
        <VisibilityIcon />
      </IconButton>
    </Box>
  );
};

// Renderer functions moved OUTSIDE parent component
const renderStatusCell = (value: string | boolean) => (
  <StatusCell value={value} />
);

const renderDateCell = (value: DateInput) => <DateCell value={value} />;

const renderActionCell = (tableMeta: any, navigate: (path: string) => void) => {
  const subscription =
    tableMeta?.tableData[
      tableMeta?.currentTableData[tableMeta.rowIndex]?.index
    ];

  return (
    <ActionCell
      subscriptionId={subscription?.subscription_id}
      navigate={navigate}
    />
  );
};

const BranchHeadSubscriptions = () => {
  const user = useSelector((state: RootState) => state.auth.user);
  const navigate = useNavigate();
  const [rows, setRows] = React.useState([]);
  const [error, setError] = React.useState<AxiosError | null>(null);
  const [assetTypes, setAssetTypes] = React.useState(null);
  const [assetTypeFilter, setAssetTypeFilter] =
    React.useState<AssetTypeItem | null>(null);

  const columns: MUIDataTableColumn[] = React.useMemo(
    () => [
      { name: "name", label: "Customer" },
      { name: "division_name", label: "Division" },
      { name: "asset_service_id", label: "Asset Id" },
      { name: "asset_type_name", label: "Asset Type" },
      { name: "subscription_type", label: "Subscription Type" },
      {
        name: "start_date",
        label: "Start Date",
        options: {
          filter: false,
          customBodyRender: renderDateCell,
        },
      },
      {
        name: "end_date",
        label: "End Date",
        options: {
          filter: false,
          customBodyRender: renderDateCell,
        },
      },
      {
        name: "is_active",
        label: "Status",
        options: {
          filter: false,
          customBodyRender: renderStatusCell,
        },
      },
      {
        name: "id",
        label: "Actions",
        options: {
          filter: false,
          sort: false,
          customBodyRender: (value, tableMeta) =>
            renderActionCell(tableMeta, navigate),
        },
      },
    ],
    [navigate]
  );

  const fetchData = React.useCallback(async () => {
    try {
      const response = await axios.get(
        `/subscription/branch-head/${user?.userId}`,
        {
          params: {
            filters: {
              asset_type_id: assetTypeFilter?.asset_type_id,
            },
          },
        }
      );

      setRows(response?.data);
    } catch (error) {
      if (error instanceof AxiosError && error) {
        setError(error);
      }
    }
  }, [assetTypeFilter, user?.userId]);

  React.useEffect(() => {
    fetchData();
  }, [assetTypeFilter, fetchData]);

  const fetchAssets = async () => {
    try {
      const assetTypesData = await axios.get("/asset-types");
      setAssetTypes(assetTypesData?.data?.list);
    } catch (error) {
      console.error("Error: ", error);
    }
  };

  React.useEffect(() => {
    fetchAssets();
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Card
        style={{ padding: "24px", margin: "16px auto", maxWidth: "2000px" }}
      >
        <Grid
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
          }}
        >
          <FormControl sx={{ marginBottom: "3px" }}>
            <SoftTypography
              variant="caption"
              fontWeight="bold"
              sx={{ paddingBottom: "10px" }}
            >
              Asset Types
            </SoftTypography>
            <Autocomplete
              options={assetTypes ?? []}
              getOptionLabel={(option) => option.asset_type_name}
              value={assetTypeFilter}
              onChange={(event, value) => {
                setAssetTypeFilter(value);
              }}
              renderInput={(params) => (
                <TextField {...params} variant="outlined" />
              )}
            />
          </FormControl>
        </Grid>
        <SoftBox
          sx={{
            "& .MuiTableRow-root:not(:last-child)": {
              "& td": (theme: Theme) => ({
                borderBottom: `${theme.borders.borderWidth[1]} solid ${theme.borders.borderColor}`,
              }),
            },
          }}
          className="manage-subscriptions"
        >
          <ThemeProvider theme={getMuiTheme}>
            <MUIDataTable
              title={"Manage Subscriptions"}
              data={rows}
              columns={columns}
              options={tableOptions}
            />
          </ThemeProvider>
          {error && <div>Error: {error.message}</div>}
        </SoftBox>
      </Card>
    </DashboardLayout>
  );
};

export default BranchHeadSubscriptions;
